import apiClient from './ApiClient';

export class CompanyRepository {
	constructor(client) {
		this.client = client;
		this.basePath = '/api/companies';
	}

	/**
	 * Get company by path (public endpoint)
	 * @param {string} companyPath - URL-friendly company identifier (e.g., "cute-claw")
	 * @returns {Promise<Object>} Company details
	 */
	async getByPath(companyPath) {
		return this.client.get(`${this.basePath}/by-path/${companyPath}`);
	}

	/**
	 * Get categories for a specific company (public endpoint)
	 * @param {string} companyPath - URL-friendly company identifier
	 * @param {boolean} activeOnly - Show only active categories (default: true)
	 * @returns {Promise<Array>} Array of categories
	 */
	async getCategories(companyPath, activeOnly = true) {
		return this.client.get(`${this.basePath}/${companyPath}/categories`, {
			activeOnly
		});
	}

	/**
	 * Get service items for a specific company (public endpoint)
	 * @param {string} companyPath - URL-friendly company identifier
	 * @param {Object} params - Optional query parameters
	 * @param {string} params.type - Filter by type ('service' or 'product')
	 * @param {string} params.categoryId - Filter by category UUID
	 * @returns {Promise<Array>} Array of service items
	 */
	async getServices(companyPath, params = {}) {
		return this.client.get(`${this.basePath}/${companyPath}/service-items`, params);
	}

	/**
	 * Get company settings (authenticated endpoint)
	 * @returns {Promise<Object>} Full company settings and data
	 */
	async getSettings() {
		return this.client.get(`${this.basePath}/settings`);
	}

	/**
	 * Update company basic details
	 * @param {Object} data - Company details to update
	 * @returns {Promise<{success: boolean}>} Updated company details
	 */
	async updateCompanyDetails(data) {
		return this.client.put(`${this.basePath}`, data);
	}

	/**
	 * Update social media links
	 * @param {Object} data - Social media links
	 * @returns {Promise<{success: boolean}>} Updated social media configuration
	 */
	async updateSocialMediaLinks(data) {
		return this.client.put(`${this.basePath}/social-links`, data);
	}

	/**
	 * Update company policies (cancellation, arrival, preparation, payment methods)
	 * @param {Object} data - Policies configuration
	 * @returns {Promise<{success: boolean}>} Updated policies
	 */
	async updatePolicies(data) {
		return this.client.put(`${this.basePath}/client-protocol`, data);
	}

	/**
	 * Update billing settings (currency, taxes, payment gateways)
	 * @param {Object} data - Billing configuration
	 * @returns {Promise<Object>} Updated billing settings
	 */
	async updateBilling(data) {
		return this.client.post(`${this.basePath}/settings/billing`, data);
	}

	/**
	 * Update booking settings (time retention, advance booking, reminders, etc.)
	 * @param {Object} data - Booking configuration
	 * @returns {Promise<{success: boolean}>} Updated booking settings
	 */
	async updateBookingSettings(data) {
		return this.client.put(`${this.basePath}/booking-rule`, data);
	}

	/**
	 * Update other settings (appearance, business hours, notifications, integrations)
	 * @param {Object} data - Other settings configuration
	 * @returns {Promise<Object>} Updated other settings
	 */
	async updateOtherSettings(data) {
		return this.client.post(`${this.basePath}/settings/other`, data);
	}

	/**
     * Close the business — marks company as inactive.
     * Clients will see a closed message on the booking page.
     * @returns {Promise<{success: boolean, message: string}>} Result indicating successful closure
     */
    async closeBusiness() {
        return this.client.patch(`${this.basePath}/close`);
    }

    /**
     * Restore the business — marks company as active again.
     * @returns {Promise<{success: boolean, message: string}>} Result indicating successful restoration
     */
    async restoreBusiness() {
        return this.client.patch(`${this.basePath}/restore`);
    }

    /**
     * Get list of Ghanaian banks for payment setup
     * @returns {Promise<Array<{name: string, code: string}>>}
     */
    async getBanks() {
        return this.client.get(`${this.basePath}/banks`);
    }

    /**
     * Get current payment setup status
     * @returns {Promise<{paymentEnabled: boolean, requiresPayment: boolean, accountNumber: string, accountName: string, bankCode: string}>}
     */
    async getPaymentSetup() {
        return this.client.get(`${this.basePath}/payment-setup`);
    }

    /**
     * Set up payment processing (creates Paystack subaccount)
     * @param {Object} payload
     * @param {string} payload.accountNumber
     * @param {string} payload.bankCode
     * @param {boolean} payload.requiresPayment
     * @returns {Promise<{success: boolean}>}
     */
    async setupPayment(payload) {
        return this.client.post(`${this.basePath}/payment-setup`, payload);
    }

    /**
     * Update the ongoing customer payment requirement without replacing payout details.
     * @param {{requiresPayment: boolean, upfrontPaymentPercentage: number}} payload
     */
    async updatePaymentRequirement(payload) {
        return this.client.patch(`${this.basePath}/payment-requirement`, payload);
    }
}

export default new CompanyRepository(apiClient);
